/**
 * PageTitle — the consistent top-of-page header.
 *
 * Replaces the per-page heading code currently scattered across every screen.
 *
 *   <PageTitle
 *     breadcrumb={['Trang chủ', 'Tài chính', 'Thanh toán']}
 *     title="Quản lý thanh toán"
 *     subtitle="Tháng 5, 2026 · 64 hoá đơn"
 *     actions={
 *       <>
 *         <Button kind="ghost" iconLeft={<Download />}>Xuất CSV</Button>
 *         <Button kind="primary" iconLeft={<Plus />}>Tạo hoá đơn</Button>
 *       </>
 *     }
 *   />
 */

import React from 'react';
import { ChevronRight } from './icons';

export function PageTitle({
  breadcrumb,
  title,
  subtitle,
  actions,
  align = 'between',     // 'between' | 'start' (actions below title on narrow)
  className = '',
}) {
  return (
    <header className={['flex flex-col gap-3 py-4 px-5 lg:px-6', className].join(' ')}>
      {breadcrumb && breadcrumb.length > 0 && <Breadcrumb items={breadcrumb} />}
      <div className={['flex gap-4 flex-wrap',
        align === 'between' ? 'items-start sm:items-end justify-between' : 'items-start'].join(' ')}>
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold leading-tight text-stone-900 -tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-stone-600">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </header>
  );
}

export function Breadcrumb({ items, className = '' }) {
  return (
    <nav aria-label="Breadcrumb" className={['flex items-center gap-1 text-xs', className].join(' ')}>
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        const label = typeof item === 'string' ? item : item.label;
        const href  = typeof item === 'string' ? null    : item.href;
        return (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight size={11} className="text-stone-300 mx-0.5" />}
            {isLast ? (
              <span className="text-stone-900 font-medium">{label}</span>
            ) : href ? (
              <a href={href} className="text-stone-500 hover:text-stone-900">{label}</a>
            ) : (
              <span className="text-stone-500">{label}</span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

/**
 * Toolbar — secondary action bar used above tables, drawers, sections.
 * Title + filter chips + buttons.
 *
 *   <Toolbar
 *     title={<><span className="font-semibold">12 hoá đơn</span> đã chọn</>}
 *     start={<><Tag onRemove={...}>Tháng: 05/2026</Tag></>}
 *     end={<><Button>Bộ lọc</Button></>}
 *   />
 */
export function Toolbar({ title, start, end, sticky = false, dark = false, className = '' }) {
  return (
    <div className={[
      'flex flex-wrap items-center gap-2 px-3 py-2 text-sm',
      dark
        ? 'bg-stone-900 text-white rounded-md'
        : 'bg-white border-b border-stone-200',
      sticky && 'sticky top-0 z-10',
      className,
    ].filter(Boolean).join(' ')}>
      {title && <div className="font-medium">{title}</div>}
      {start && <div className="flex items-center gap-2">{start}</div>}
      <div className="flex-1" />
      {end && <div className="flex items-center gap-2">{end}</div>}
    </div>
  );
}
